import validator from 'validator';
import moment from 'moment';
import appStore from 'client/AppStore';
import {pushQueryParams} from 'client/history';
import {QUESTION_TYPES} from 'client/constants';
import {QuestionStatus} from 'gql/types';
import sessionStore from 'client/Session/SessionStore';

type URLSearchParameter = {
  parameter: string;
  validator?: (value: string) => boolean;
  default?: () => string | number | null | undefined;
  defaultValue?: string | null;
};

export const SEARCH_PARAMETERS = {
  questionSetId: {
    parameter: 'questionSetId',
    validator: isValidQuestionSetId
  },
  status: {
    parameter: 'status',
    validator: isValidStatus,
    default: () => 'pending'
  },
  subject: {
    parameter: 'subject',
    validator: isValidUUID
  },
  search: {
    parameter: 'search'
  },
  author: {
    parameter: 'author',
    validator: isValidUUID,
    default: () => (sessionStore.hasContentManagerAccess() ? '' : sessionStore.getUserId())
  },
  type: {
    parameter: 'type',
    validator: isValidType
  },
  flag: {
    parameter: 'flag'
  },
  difficulty: {
    parameter: 'difficulty',
    validator: isValidDifficulty,
    default: () => '1,2,3'
  },
  sort: {
    parameter: 'sort',
    default: () => 'name'
  },
  publishDateStart: {
    parameter: 'publishDateStart',
    validator: isValidPublishDate
  },
  publishDateEnd: {
    parameter: 'publishDateEnd',
    validator: isValidPublishDate
  },
  page: {
    parameter: 'page',
    validator: isValidPage
  },
  fallbackPage: {
    parameter: 'fallbackPage',
    validator: isValidFallbackPage,
    default: () => 1
  },
  guideLevelIds: {
    parameter: 'guideLevelIds',
    validator: (str: string) => str.split(',').every((entry) => validator.isUUID(entry))
  }
};

export function getDefaultParameters() {
  return Object.entries(SEARCH_PARAMETERS).reduce<
    Record<string, string | number | null | undefined>
  >((acc, [param, options]) => {
    if ('default' in options && options.default) {
      acc[param] = options.default();
    }
    return acc;
  }, {});
}

function isValidPublishDate(date: string): boolean {
  // We can only filter by published_at if the question sets we'er looking at are published. Therefore,
  // if we're not looking at published question sets, we bail.
  if (getNormalizedStatusURLParameter() !== QuestionStatus.Published) {
    return false;
  }
  const startDate = getURLSearchParameter(SEARCH_PARAMETERS.publishDateStart);
  const endDate = getURLSearchParameter(SEARCH_PARAMETERS.publishDateEnd);
  if (startDate && endDate) {
    const momentStartDate = moment(parseInt(startDate, 10));
    const momentEndDate = moment(parseInt(endDate, 10));
    return momentStartDate.isBefore(momentEndDate);
  }
  return moment(parseInt(date, 10)).isValid();
}

function isValidDifficulty(str: string): boolean {
  const difficulties = str.split(',') || [];
  return (
    difficulties
      .map((d) => {
        return ['1', '2', '3'].includes(d);
      })
      .some((result) => result === false) !== true
  );
}

function isValidFallbackPage(int: string): boolean {
  return validator.isInt(int);
}

function isValidQuestionSetId(str: string): boolean {
  return str.split(',').some((id) => validator.isUUID(id));
}

function isValidStatus(str: string): boolean {
  if (!str) return false;
  return (Object.values(QuestionStatus) as string[]).includes(str.toUpperCase());
}

function isValidType(str: string): boolean {
  return Object.values(QUESTION_TYPES).some((type) => (type as {name: string}).name === str);
}

function isValidUUID(str: string): boolean {
  return Boolean(str);
}

function isValidURLSearchParameter(str: string): boolean {
  return Boolean(str);
}

function isValidPage(str: string): boolean {
  return validator.isInt(str) || validator.isUUID(str);
}

/**
 * The router location is passed through as a plain object (see `AppStore`), so
 * we narrow the dynamically-typed `routerProps.location` to the shape we read.
 */
function getLocationQuery(): Record<string, string> | undefined {
  const location = (appStore.routerProps as {location?: {query?: Record<string, string>}}).location;
  return location?.query;
}

export function hasValidURLSearchParameter(URLSearchParameter: URLSearchParameter): boolean {
  /**
   * If there isn't a search query (`?<search>`) or the requested
   * parameter is not in the query, then it's not valid.
   */
  const query = getLocationQuery();
  if (!query || !query[URLSearchParameter.parameter]) {
    return false;
  }
  /**
   * If the provided search parameter defines a `validator`, we use that to
   * validate. Otherwise, we use a generic validator.
   */
  const validatorFn = URLSearchParameter.validator || isValidURLSearchParameter;
  return validatorFn(query[URLSearchParameter.parameter]);
}

/**
 * Retrieve a `URLSearchParameter` value from the current URL search string.
 * @param {Object} URLSearchParameter
 * @returns {string|null} Will return `null` if there is no search string, or the requested
 * parameter is not set.
 */
export function getURLSearchParameter(URLSearchParameter: URLSearchParameter): string | null {
  const query = getLocationQuery();
  if (!query || !query[URLSearchParameter.parameter]) {
    return URLSearchParameter.defaultValue || null;
  }
  return decodeURIComponent(query[URLSearchParameter.parameter]);
}

/**
 * "set" a `URLSearchParameter` by calling `pushQueryParams` to the defined `parameter`.
 * @param {Object} URLSearchParameter
 * @param {String} value
 */
export function setURLSearchParameter(URLSearchParameter: URLSearchParameter, value: string) {
  pushQueryParams({
    [URLSearchParameter.parameter]: value
  });
}

/**
 * Returns the `?status=` URL parameter normalized to the uppercase `QuestionStatus`
 * enum value (e.g. `'published'` → `'PUBLISHED'`). Returns `null` when the param is
 * absent or invalid. Use this anywhere the status URL value is compared against
 * `QuestionStatus` or sent to the GraphQL `QuestionFilterInput.status` filter.
 */
export function getNormalizedStatusURLParameter(): QuestionStatus | null {
  const value = getURLSearchParameter(SEARCH_PARAMETERS.status);
  if (!value) return null;
  return value.toUpperCase() as QuestionStatus;
}
